import React, { useState } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Divider,
  Stack,
  Chip,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie
} from 'recharts';
import {
  TrendingUp as TrendingUpIcon,
  Weekend as WeekendIcon,
  BusinessCenter as BusinessIcon,
  EmojiEvents as TrophyIcon,
  Close as CloseIcon,
  ZoomOutMap as ExpandIcon
} from '@mui/icons-material';
import { ccyFormat } from '../../helpers/helper';

const WeeklyAnalysis = ({ stats }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expandedChart, setExpandedChart] = useState(null); // 'weekday' o 'weekend'
  const WEEKDAY_COLORS = {
    0: theme.palette.error.main,
    1: theme.palette.primary.main,
    2: theme.palette.success.main,
    3: theme.palette.warning.main,
    4: theme.palette.secondary.main,
    5: theme.palette.info.main,
    6: theme.palette.warning.light,
  };

  if (!stats || !stats.daily_sales_data || stats.daily_sales_data.length === 0) {
    return null;
  }

  // Agrupar ventas por día de la semana
  const getWeekdayAnalysis = () => {
    const weekdayMap = {
      0: { day: 'Domingo', dayNum: 0, sales: 0, revenue: 0, count: 0 },
      1: { day: 'Lunes', dayNum: 1, sales: 0, revenue: 0, count: 0 },
      2: { day: 'Martes', dayNum: 2, sales: 0, revenue: 0, count: 0 },
      3: { day: 'Miércoles', dayNum: 3, sales: 0, revenue: 0, count: 0 },
      4: { day: 'Jueves', dayNum: 4, sales: 0, revenue: 0, count: 0 },
      5: { day: 'Viernes', dayNum: 5, sales: 0, revenue: 0, count: 0 },
      6: { day: 'Sábado', dayNum: 6, sales: 0, revenue: 0, count: 0 },
    };

    stats.daily_sales_data.forEach(daySale => {
      // Parsear la fecha de forma local para evitar desfase de zona horaria
      const [year, month, day] = daySale.date.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      const dayOfWeek = date.getDay();
      
      weekdayMap[dayOfWeek].sales += daySale.total_sales || 0;
      weekdayMap[dayOfWeek].revenue += daySale.revenue || 0;
      weekdayMap[dayOfWeek].count += 1;
    });

    // Calcular promedios
    Object.keys(weekdayMap).forEach(key => {
      const data = weekdayMap[key];
      if (data.count > 0) {
        data.avgSales = data.sales / data.count;
        data.avgRevenue = data.revenue / data.count;
      }
    });

    return Object.values(weekdayMap);
  };

  // Comparar Semana vs Fin de Semana
  const getWeekendComparison = () => {
    let weekdaySales = 0;
    let weekdayRevenue = 0;
    let weekdayCount = 0;
    
    let weekendSales = 0;
    let weekendRevenue = 0;
    let weekendCount = 0;

    stats.daily_sales_data.forEach(daySale => {
      // Parsear la fecha de forma local para evitar desfase de zona horaria
      const [year, month, day] = daySale.date.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      const dayOfWeek = date.getDay();
      
      if (dayOfWeek === 0 || dayOfWeek === 6) { // Domingo o Sábado
        weekendSales += daySale.total_sales || 0;
        weekendRevenue += daySale.revenue || 0;
        weekendCount += 1;
      } else {
        weekdaySales += daySale.total_sales || 0;
        weekdayRevenue += daySale.revenue || 0;
        weekdayCount += 1;
      }
    });

    return {
      weekday: {
        sales: weekdaySales,
        revenue: weekdayRevenue,
        count: weekdayCount,
        avgSales: weekdayCount > 0 ? weekdaySales / weekdayCount : 0,
        avgRevenue: weekdayCount > 0 ? weekdayRevenue / weekdayCount : 0
      },
      weekend: {
        sales: weekendSales,
        revenue: weekendRevenue,
        count: weekendCount,
        avgSales: weekendCount > 0 ? weekendSales / weekendCount : 0,
        avgRevenue: weekendCount > 0 ? weekendRevenue / weekendCount : 0
      }
    };
  };

  const weekdayData = getWeekdayAnalysis();
  const weekendComparison = getWeekendComparison();

  // Encontrar el mejor día de la semana
  const bestDay = weekdayData.reduce((max, day) => 
    day.sales > max.sales ? day : max
  , weekdayData[0]);

  // Datos para pie chart
  const pieData = [
    { name: 'Semana (L-V)', value: weekendComparison.weekday.sales, color: theme.palette.primary.main },
    { name: 'Fin de Semana (S-D)', value: weekendComparison.weekend.sales, color: theme.palette.warning.light }
  ];

  // Tooltip personalizado
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 2, border: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            {payload[0].payload.day}
          </Typography>
          <Typography variant="body2" color="primary">
            Ventas: {payload[0].value.toFixed(0)}
          </Typography>
          <Typography variant="body2" color="success.main">
            Ingresos: ${ccyFormat(payload[0].payload.revenue)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Promedio: {payload[0].payload.avgSales?.toFixed(1)} ventas/día
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  return (
    <Box sx={{
      WebkitTapHighlightColor: 'transparent',
      '& .recharts-wrapper': {
        WebkitTapHighlightColor: 'transparent',
      }
    }}>
      <Paper elevation={0} sx={{ 
        p: isMobile ? 2 : 3, 
        mb: 4, 
        borderRadius: 3, 
        border: `1px solid ${theme.palette.divider}`,
        WebkitTapHighlightColor: 'transparent'
      }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <TrendingUpIcon sx={{ color: 'primary.main', fontSize: isMobile ? 24 : 28 }} />
          <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={600}>
            Análisis de Patrones Semanales
          </Typography>
        </Stack>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={isMobile ? 2 : 3}>
          {/* Gráfico de barras por día de la semana */}
          <Grid item xs={12} lg={8}>
            <Box sx={{ position: 'relative' }}>
              {isMobile && (
                <IconButton
                  onClick={() => setExpandedChart('weekday')}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    zIndex: 1,
                    bgcolor: alpha(theme.palette.background.paper, 0.9),
                    boxShadow: 1,
                    WebkitTapHighlightColor: 'transparent',
                    '&:hover': {
                      bgcolor: theme.palette.background.paper,
                    }
                  }}
                  size="small"
                >
                  <ExpandIcon fontSize="small" />
                </IconButton>
              )}
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <TrophyIcon sx={{ color: 'warning.light', fontSize: isMobile ? 18 : 20 }} />
                <Typography variant={isMobile ? "body1" : "subtitle1"} fontWeight={600}>
                  Ventas por Día de la Semana
                </Typography>
              </Stack>
              
              {bestDay && (
                <Chip
                  icon={<TrophyIcon />}
                  label={`Mejor día: ${bestDay.day} (${bestDay.sales} ventas)`}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    mb: 2,
                    bgcolor: WEEKDAY_COLORS[bestDay.dayNum],
                    WebkitTapHighlightColor: 'transparent',
                    color: 'common.white',
                    fontWeight: 600,
                    '& .MuiChip-icon': { color: 'common.white' }
                  }}
                />
              )}

              <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                <BarChart data={weekdayData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis 
                    dataKey="day" 
                    tick={{ fontSize: isMobile ? 9 : 11 }}
                    angle={isMobile ? -45 : -45}
                    textAnchor="end"
                    height={isMobile ? 60 : 80}
                  />
                  <YAxis tick={{ fontSize: isMobile ? 10 : 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  {!isMobile && <Legend />}
                  <Bar dataKey="sales" name="Total Ventas" radius={[8, 8, 0, 0]}>
                    {weekdayData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={WEEKDAY_COLORS[entry.dayNum]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Grid>

          {/* Comparación Semana vs Fin de Semana */}
          <Grid item xs={12} lg={4}>
            <Box sx={{ position: 'relative' }}>
              {isMobile && (
                <IconButton
                  onClick={() => setExpandedChart('weekend')}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    zIndex: 1,
                    bgcolor: alpha(theme.palette.background.paper, 0.9),
                    boxShadow: 1,
                    WebkitTapHighlightColor: 'transparent',
                    '&:hover': {
                      bgcolor: theme.palette.background.paper,
                    }
                  }}
                  size="small"
                >
                  <ExpandIcon fontSize="small" />
                </IconButton>
              )}
              <Typography variant={isMobile ? "body1" : "subtitle1"} fontWeight={600} gutterBottom>
                Semana vs Fin de Semana
              </Typography>
              
              <ResponsiveContainer width="100%" height={isMobile ? 180 : 200}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={isMobile ? false : ({ name, percent }) => `${name.split(' ')[0]}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={isMobile ? 60 : 80}
                    fill={theme.palette.secondary.main}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>

              <Stack spacing={2} mt={2}>
                {/* Card Semana */}
                <Box
                  sx={{
                    p: isMobile ? 1.5 : 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    WebkitTapHighlightColor: 'transparent'
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <BusinessIcon sx={{ color: 'primary.main', fontSize: isMobile ? 18 : 20 }} />
                    <Typography variant="body2" fontWeight={600} fontSize={isMobile ? '0.8rem' : undefined}>
                      Lunes - Viernes
                    </Typography>
                  </Stack>
                  <Typography variant={isMobile ? "h6" : "h5"} fontWeight={700} color="primary.main">
                    {weekendComparison.weekday.sales}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontSize={isMobile ? '0.7rem' : undefined}>
                    ${ccyFormat(weekendComparison.weekday.revenue)} total
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary" fontSize={isMobile ? '0.7rem' : undefined}>
                    Promedio: {weekendComparison.weekday.avgSales.toFixed(1)} ventas/día
                  </Typography>
                </Box>

                {/* Card Fin de Semana */}
                <Box
                  sx={{
                    p: isMobile ? 1.5 : 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.warning.light, 0.15),
                    border: `1px solid ${alpha(theme.palette.warning.light, 0.3)}`,
                    WebkitTapHighlightColor: 'transparent'
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <WeekendIcon sx={{ color: 'warning.light', fontSize: isMobile ? 18 : 20 }} />
                    <Typography variant="body2" fontWeight={600} fontSize={isMobile ? '0.8rem' : undefined}>
                      Sábado - Domingo
                    </Typography>
                  </Stack>
                  <Typography variant={isMobile ? "h6" : "h5"} fontWeight={700} color="warning.main">
                    {weekendComparison.weekend.sales}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontSize={isMobile ? '0.7rem' : undefined}>
                    ${ccyFormat(weekendComparison.weekend.revenue)} total
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary" fontSize={isMobile ? '0.7rem' : undefined}>
                    Promedio: {weekendComparison.weekend.avgSales.toFixed(1)} ventas/día
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Grid>
        </Grid>

        {/* Insights adicionales */}
        <Box 
          mt={3} 
          p={isMobile ? 1.5 : 2} 
          sx={{ 
            bgcolor: 'action.hover', 
            borderRadius: 2,
            WebkitTapHighlightColor: 'transparent'
          }}
        >
          <Typography variant="body2" color="text.secondary" gutterBottom fontSize={isMobile ? '0.8rem' : undefined}>
            📊 <strong>Insights:</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary" fontSize={isMobile ? '0.7rem' : undefined}>
            • El día con más ventas es <strong>{bestDay?.day}</strong> con {bestDay?.sales} ventas totales<br/>
            • Los días de semana representan el <strong>
              {((weekendComparison.weekday.sales / (weekendComparison.weekday.sales + weekendComparison.weekend.sales)) * 100).toFixed(1)}%
            </strong> de las ventas<br/>
            • Promedio diario: <strong>
              {weekendComparison.weekday.avgSales > weekendComparison.weekend.avgSales 
                ? 'Mayor en días de semana' 
                : 'Mayor en fin de semana'}
            </strong> ({Math.max(weekendComparison.weekday.avgSales, weekendComparison.weekend.avgSales).toFixed(1)} ventas/día)
          </Typography>
        </Box>
      </Paper>

      {/* Modales para expandir gráficos en móvil */}
      <Dialog
        open={expandedChart === 'weekday'}
        onClose={() => setExpandedChart(null)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            WebkitTapHighlightColor: 'transparent',
            bgcolor: 'background.default'
          }
        }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              Ventas por Día de la Semana
            </Typography>
            <IconButton 
              onClick={() => setExpandedChart(null)}
              sx={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {bestDay && (
            <Chip
              icon={<TrophyIcon />}
              label={`Mejor día: ${bestDay.day} (${bestDay.sales} ventas)`}
              sx={{
                mb: 2,
                bgcolor: WEEKDAY_COLORS[bestDay.dayNum],
                WebkitTapHighlightColor: 'transparent',
                color: 'common.white',
                fontWeight: 600,
                '& .MuiChip-icon': { color: 'common.white' }
              }}
            />
          )}
          
          <Box sx={{ overflowX: 'auto', overflowY: 'hidden', pb: 2 }}>
            <Box sx={{ minWidth: 600 }}>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={weekdayData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis 
                    dataKey="day" 
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="sales" name="Total Ventas" radius={[8, 8, 0, 0]}>
                    {weekdayData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={WEEKDAY_COLORS[entry.dayNum]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={expandedChart === 'weekend'}
        onClose={() => setExpandedChart(null)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            WebkitTapHighlightColor: 'transparent',
            bgcolor: 'background.default'
          }
        }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              Semana vs Fin de Semana
            </Typography>
            <IconButton 
              onClick={() => setExpandedChart(null)}
              sx={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={true}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill={theme.palette.secondary.main}
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>

          <Stack spacing={2} mt={3}>
            {/* Card Semana */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                WebkitTapHighlightColor: 'transparent'
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <BusinessIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="body2" fontWeight={600}>
                  Lunes - Viernes
                </Typography>
              </Stack>
              <Typography variant="h5" fontWeight={700} color="primary.main">
                {weekendComparison.weekday.sales}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ${ccyFormat(weekendComparison.weekday.revenue)} total
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary">
                Promedio: {weekendComparison.weekday.avgSales.toFixed(1)} ventas/día
              </Typography>
            </Box>

            {/* Card Fin de Semana */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.warning.light, 0.15),
                border: `1px solid ${alpha(theme.palette.warning.light, 0.3)}`,
                WebkitTapHighlightColor: 'transparent'
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <WeekendIcon sx={{ color: 'warning.light', fontSize: 20 }} />
                <Typography variant="body2" fontWeight={600}>
                  Sábado - Domingo
                </Typography>
              </Stack>
              <Typography variant="h5" fontWeight={700} color="warning.main">
                {weekendComparison.weekend.sales}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ${ccyFormat(weekendComparison.weekend.revenue)} total
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary">
                Promedio: {weekendComparison.weekend.avgSales.toFixed(1)} ventas/día
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default WeeklyAnalysis;
